const TZ = 'Asia/Hong_Kong';

const dateParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function parts(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p: Record<string, string> = {};
  for (const x of dateParts.formatToParts(d)) p[x.type] = x.value;
  return p;
}

/** 2026-10-22 (Hong Kong time). */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const p = parts(iso);
  return p ? `${p.year}-${p.month}-${p.day}` : '—';
}

/** 2026-10-22 14:30 (Hong Kong time). */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = parts(iso);
  return p ? `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}` : '—';
}

export function fmtNumber(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('en-HK');
}

/** Period text such as "2026-10-01 10:00 至 2026-10-31 23:59", "即時開始", "不設結束". */
export function fmtPeriod(start: string | null, end: string | null): string {
  if (!start && !end) return '不限時間';
  if (start && !end) return `${fmtDateTime(start)} 起`;
  if (!start && end) return `至 ${fmtDateTime(end)}`;
  return `${fmtDateTime(start)} 至 ${fmtDateTime(end)}`;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO time → value for <input type="datetime-local"> in the browser's time zone. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO time, or null when empty. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Today in Hong Kong as YYYY-MM-DD. */
export function todayHK(): string {
  return fmtDate(new Date().toISOString());
}

/** YYYY-MM-DD plus `days` (may be negative). */
export function addDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The default report range: the last 30 days including today. */
export function last30Days(): { from: string; to: string } {
  const to = todayHK();
  return { from: addDays(to, -29), to };
}

/** "0.4567" → "45.7%"; null → "—". */
export function fmtPercent(share: number | null | undefined): string {
  return share == null || Number.isNaN(share) ? '—' : `${(share * 100).toLocaleString('en-HK', { maximumFractionDigits: 1 })}%`;
}

/** Gift card code in groups of four: XXXX-XXXX-XXXX. */
export function fmtGiftCode(code: string): string {
  const c = code.replace(/[\s-]/g, '').toUpperCase();
  return c.match(/.{1,4}/g)?.join('-') ?? c;
}
